import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { X, Plus, Search } from "lucide-react";
import type { RuleFilter, RuleOperator } from "@/hooks/useTagAutoRules";
import { supabase } from "@/integrations/supabase/client";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { displayAdminEventLabel } from "@/lib/datetime";

interface TagRuleFormProps {
  initialFilters?: RuleFilter[];
  onSubmit: (filters: RuleFilter[]) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

type FieldType = "number" | "text" | "select" | "boolean" | "event_tag" | "event";

interface FieldConfig {
  value: string;
  label: string;
  type: FieldType;
  options?: string[];
}

const FILTER_FIELDS: FieldConfig[] = [
  { value: "attended_event_tag", label: "Attended Events with Tag", type: "event_tag" },
  { value: "attended_event", label: "Attended Specific Event", type: "event" },
  { value: "credits_balance", label: "Credits Balance", type: "number" },
  { value: "days_since_joined", label: "Days Since Joined", type: "number" },
  { value: "days_since_last_active", label: "Days Since Last Active", type: "number" },
  { value: "days_since_last_purchase", label: "Days Since Last Purchase", type: "number" },
  { value: "email", label: "Email", type: "text" },
  { value: "events_count", label: "Events Count", type: "number" },
  { value: "first_name", label: "First Name", type: "text" },
  { value: "inactive_days", label: "Inactive Days", type: "number" },
  { value: "last_name", label: "Last Name", type: "text" },
  {
    value: "member_type",
    label: "Member Type",
    type: "select",
    options: ["Founding", "Community", "Other"],
  },
  { value: "phone", label: "Phone", type: "text" },
  { value: "referral_code", label: "Referral Code", type: "text" },
  {
    value: "role",
    label: "Role",
    type: "select",
    options: ["admin", "user"],
  },
  { value: "is_unlimited", label: "Unlimited Status", type: "boolean" },
];

interface OperatorConfig {
  value: RuleOperator;
  symbol: string;
  label: string;
  types: FieldType[];
}

const OPERATORS: OperatorConfig[] = [
  { value: "=", symbol: "=", label: "equals", types: ["number", "text", "select", "event_tag", "event"] },
  { value: "<>", symbol: "<>", label: "not equal", types: ["number", "text", "select", "event_tag", "event"] },
  { value: ">", symbol: ">", label: "greater than", types: ["number", "event_tag", "event"] },
  { value: "<", symbol: "<", label: "less than", types: ["number", "event_tag", "event"] },
  { value: ">=", symbol: ">=", label: "at least", types: ["number", "event_tag", "event"] },
  { value: "<=", symbol: "<=", label: "at most", types: ["number", "event_tag", "event"] },
  { value: "~~", symbol: "~~", label: "like operator", types: ["text"] },
  { value: "~~*", symbol: "~~*", label: "ilike operator", types: ["text"] },
  { value: "in", symbol: "in", label: "one of a list of values", types: ["text", "select"] },
  { value: "is", symbol: "is", label: "checking for (null,not null,true,false)", types: ["boolean"] },
  { value: "len>", symbol: "len>", label: "length greater than", types: ["text"] },
  { value: "len<", symbol: "len<", label: "length less than", types: ["text"] },
  { value: "len>=", symbol: "len>=", label: "length greater than or equal", types: ["text"] },
  { value: "len<=", symbol: "len<=", label: "length less than or equal", types: ["text"] },
  { value: "len=", symbol: "len=", label: "length equals", types: ["text"] },
];

interface FilterRow {
  id: string;
  field: string;
  operator: RuleOperator;
  value: string;
}

const createEmptyRow = (): FilterRow => ({
  id: crypto.randomUUID(),
  field: "events_count",
  operator: "=",
  value: "",
});

const getAvailableOperators = (field: string) => {
  const fieldConfig = FILTER_FIELDS.find((f) => f.value === field);
  return fieldConfig ? OPERATORS.filter((op) => op.types.includes(fieldConfig.type)) : OPERATORS;
};

export function TagRuleForm({
  initialFilters = [],
  onSubmit,
  onCancel,
  isSubmitting,
}: TagRuleFormProps) {
  // Convert initial filters to rows
  const initialRows: FilterRow[] = initialFilters.length > 0
    ? initialFilters.map((f) => ({ ...f, id: crypto.randomUUID() }))
    : [createEmptyRow()];

  const [rows, setRows] = useState<FilterRow[]>(initialRows);
  const [eventSearchOpen, setEventSearchOpen] = useState<string | null>(null);

  // Fetch unique event tags
  const { data: eventTags = [] } = useQuery({
    queryKey: ["event-tags-list"],
    queryFn: async () => {
      const { data } = await supabase
        .from("event_tags")
        .select("tag")
        .order("tag");
      return [...new Set(data?.map((t) => t.tag) || [])];
    },
  });

  // Fetch events for dropdown
  const { data: events = [] } = useQuery({
    queryKey: ["events-list-for-rules"],
    queryFn: async () => {
      const { data } = await supabase
        .from("events")
        .select("id, title, start_time, timezone")
        .order("start_time", { ascending: false });
      return data || [];
    },
  });

  const handleFieldChange = (rowId: string, field: string) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row.id !== rowId) return row;
        const availableOps = getAvailableOperators(field);
        const newOperator = availableOps.find((op) => op.value === row.operator)
          ? row.operator
          : availableOps[0]?.value || "=";
        return { ...row, field, operator: newOperator, value: "" };
      })
    );
  };

  const handleOperatorChange = (rowId: string, operator: RuleOperator) => {
    setRows((prev) =>
      prev.map((row) => (row.id === rowId ? { ...row, operator } : row))
    );
  };

  const handleValueChange = (rowId: string, value: string) => {
    setRows((prev) =>
      prev.map((row) => (row.id === rowId ? { ...row, value } : row))
    );
  };

  const handleAddRow = () => {
    setRows((prev) => [...prev, createEmptyRow()]);
  };

  const handleRemoveRow = (rowId: string) => {
    setRows((prev) => {
      const filtered = prev.filter((row) => row.id !== rowId);
      // Keep at least one row
      return filtered.length > 0 ? filtered : [createEmptyRow()];
    });
  };

  const handleSubmit = () => {
    // Filter out empty rows and convert to RuleFilter format
    const validFilters: RuleFilter[] = rows
      .filter((row) => row.field && row.operator && row.value)
      .map(({ field, operator, value }) => ({ field, operator, value }));

    if (validFilters.length === 0) return;
    onSubmit(validFilters);
  };

  const renderValueInput = (row: FilterRow) => {
    const fieldConfig = FILTER_FIELDS.find((f) => f.value === row.field);
    if (!fieldConfig) return null;

    // Event tag field - show dropdown of event tags + count input
    if (fieldConfig.type === "event_tag") {
      // Value format: "count|tagName"
      const [countPart, tagPart] = (row.value || "1|").split("|");
      return (
        <div className="flex flex-1 gap-2">
          <Input
            type="number"
            min="1"
            placeholder="Count"
            value={countPart || "1"}
            onChange={(e) => handleValueChange(row.id, `${e.target.value}|${tagPart || ""}`)}
            className="w-20 bg-background"
          />
          <Select
            value={tagPart || ""}
            onValueChange={(v) => handleValueChange(row.id, `${countPart || "1"}|${v}`)}
          >
            <SelectTrigger className="flex-1 bg-background">
              <SelectValue placeholder="Select event tag" />
            </SelectTrigger>
            <SelectContent className="bg-popover">
              {eventTags.map((tag) => (
                <SelectItem key={tag} value={tag}>
                  {tag}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    }

    // Specific event field - searchable dropdown + count input
    if (fieldConfig.type === "event") {
      // Value format: "count|eventId"
      const [countPart, eventId] = (row.value || "1|").split("|");
      const selectedEvent = events.find((e) => e.id === eventId);
      return (
        <div className="flex flex-1 gap-2">
          <Input
            type="number"
            min="1"
            placeholder="Count"
            value={countPart || "1"}
            onChange={(e) => handleValueChange(row.id, `${e.target.value}|${eventId || ""}`)}
            className="w-20 bg-background"
          />
          <Popover
            open={eventSearchOpen === row.id}
            onOpenChange={(open) => setEventSearchOpen(open ? row.id : null)}
          >
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                className="flex-1 justify-between bg-background font-normal"
              >
                <span className="truncate">
                   {selectedEvent
                     ? displayAdminEventLabel(selectedEvent.title, selectedEvent.start_time, selectedEvent.timezone || undefined)
                     : "Select event..."}
                </span>
                <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[400px] p-0" align="start">
              <Command>
                <CommandInput placeholder="Search events..." />
                <CommandList>
                  <CommandEmpty>No events found.</CommandEmpty>
                  <CommandGroup className="max-h-[300px] overflow-auto">
                    {events.map((event) => (
                      <CommandItem
                        key={event.id}
                         value={displayAdminEventLabel(event.title, event.start_time, event.timezone || undefined)}
                        onSelect={() => {
                          handleValueChange(row.id, `${countPart || "1"}|${event.id}`);
                          setEventSearchOpen(null);
                        }}
                      >
                        <span className="font-medium">
                          {displayAdminEventLabel(event.title, event.start_time, event.timezone || undefined)}
                        </span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      );
    }

    // Boolean field with "is" operator
    if (fieldConfig.type === "boolean" && row.operator === "is") {
      return (
        <Select value={row.value} onValueChange={(v) => handleValueChange(row.id, v)}>
          <SelectTrigger className="flex-1 bg-background">
            <SelectValue placeholder="Enter a value" />
          </SelectTrigger>
          <SelectContent className="bg-popover">
            <SelectItem value="true">true</SelectItem>
            <SelectItem value="false">false</SelectItem>
            <SelectItem value="null">null</SelectItem>
            <SelectItem value="not null">not null</SelectItem>
          </SelectContent>
        </Select>
      );
    }

    // Select field with specific options (not "in" operator)
    if (fieldConfig.type === "select" && fieldConfig.options && row.operator !== "in") {
      return (
        <Select value={row.value} onValueChange={(v) => handleValueChange(row.id, v)}>
          <SelectTrigger className="flex-1 bg-background">
            <SelectValue placeholder="Enter a value" />
          </SelectTrigger>
          <SelectContent className="bg-popover">
            {fieldConfig.options.map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    // Default text/number input
    return (
      <Input
        type={fieldConfig.type === "number" ? "number" : "text"}
        placeholder="Enter a value"
        value={row.value}
        onChange={(e) => handleValueChange(row.id, e.target.value)}
        className="flex-1 bg-background"
      />
    );
  };

  const validFilterCount = rows.filter((r) => r.field && r.operator && r.value).length;

  return (
    <div className="space-y-4">
      {/* Filter rows */}
      <div className="space-y-1">
        {rows.map((row, index) => {
          const availableOperators = getAvailableOperators(row.field);
          return (
            <div key={row.id}>
              {index > 0 && (
                <div className="text-xs text-muted-foreground font-medium pl-2 py-1">AND</div>
              )}
              <div className="flex items-center gap-2">
                {/* Field selector */}
                <Select value={row.field} onValueChange={(v) => handleFieldChange(row.id, v)}>
                  <SelectTrigger className="w-[160px] bg-background">
                    <SelectValue placeholder="Property" />
                  </SelectTrigger>
                  <SelectContent className="bg-popover">
                    {FILTER_FIELDS.map((field) => (
                      <SelectItem key={field.value} value={field.value}>
                        {field.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Operator selector */}
                <Select
                  value={row.operator}
                  onValueChange={(v) => handleOperatorChange(row.id, v as RuleOperator)}
                >
                  <SelectTrigger className="w-[70px] bg-background font-mono">
                    <SelectValue>{row.operator}</SelectValue>
                  </SelectTrigger>
                  <SelectContent className="bg-popover min-w-[260px]">
                    {availableOperators.map((op) => (
                      <SelectItem key={op.value} value={op.value}>
                        <span className="flex items-center gap-3">
                          <span className="font-mono text-muted-foreground whitespace-nowrap">[{op.symbol}]</span>
                          <span>{op.label}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Value input */}
                {renderValueInput(row)}

                {/* Remove button */}
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  onClick={() => handleRemoveRow(row.id)}
                  className="shrink-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Add condition button */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={handleAddRow}
        className="text-muted-foreground"
      >
        <Plus className="h-4 w-4 mr-1" />
        Add Condition (AND)
      </Button>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={validFilterCount === 0 || isSubmitting}
        >
          {isSubmitting ? "Saving..." : "Save Filter"}
        </Button>
      </div>
    </div>
  );
}
